import { useState } from "react";
import { useFormikContext } from "formik";
import { FinancialAccountType } from "@common/types/financialAccount";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { FormikField } from "@components/forms/FormikField/FormikField";
import {
    CounterpartyCombobox,
    type CounterpartyComboboxValue,
} from "@components/transactions/TransactionSplitV2/CounterpartyCombobox";
import { HStack } from "@design-system/HStack/HStack";
import { BankCashItemType } from "@common/types/bankCashItem";
import { getCashItemLabel } from "@common/helpers/reconciliationCashItems";
import { isCheckingAccount } from "@helpers/financialAccount";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import {
    AddTransactionTypePicker,
    TRANSACTION_TYPE,
    useOpenAddTransactionType,
} from "../AddTransactionTypePicker";
import {
    hasEnteredCashItemDetails,
    RecordCheckFormValues,
} from "./recordCheckForm";
import styles from "./RecordCheckModal.module.scss";

const fields = createFormikFields<RecordCheckFormValues>();

export const RecordCheckFormFields = () => {
    const formik = useFormikContext<RecordCheckFormValues>();
    const financialAccounts = useFinancialAccounts();
    const openAddTransactionType = useOpenAddTransactionType();
    const isCheck = formik.values.type === BankCashItemType.CHECK;
    const [selectedCounterparty, setSelectedCounterparty] =
        useState<CounterpartyComboboxValue>();

    const clearLineAccounts = () =>
        void formik.setFieldValue(
            "lines",
            formik.values.lines.map((line) => ({
                ...line,
                accountId: "",
                accrualAccountId: "",
            })),
        );

    const onTypeChange = (type: BankCashItemType) => {
        if (type !== BankCashItemType.CHECK) {
            return;
        }
        const selectedAccount = financialAccounts.find(
            (account) => account.id === formik.values.financialAccountId,
        );
        if (
            selectedAccount !== undefined &&
            selectedAccount.financialType !== FinancialAccountType.CHECKING
        ) {
            void formik.setFieldValue("financialAccountId", undefined);
            clearLineAccounts();
        }
    };

    return (
        <>
            <HStack gap={12} align="start">
                <FormikField<BankCashItemType>
                    name="type"
                    label="Type"
                    className={styles.typeField}
                    control="button"
                    required
                >
                    {({ field, helpers, invalid }) => (
                        <AddTransactionTypePicker
                            value={field.value}
                            unsavedItemLabel={
                                hasEnteredCashItemDetails({
                                    values: formik.values,
                                    initialDate: formik.initialValues.date,
                                })
                                    ? getCashItemLabel(field.value)
                                    : undefined
                            }
                            invalid={invalid}
                            onBlur={() => void helpers.setTouched(true)}
                            onValueChange={(type) => {
                                if (type === TRANSACTION_TYPE) {
                                    openAddTransactionType(type);
                                    return;
                                }
                                void helpers.setValue(type);
                                onTypeChange(type);
                            }}
                            buttonProps={{ size: "lg", block: true }}
                        />
                    )}
                </FormikField>
                <fields.FinancialAccount
                    name="financialAccountId"
                    label="Account"
                    className={styles.accountField}
                    clearable
                    emptyValue={undefined}
                    onValueChange={clearLineAccounts}
                    accountFilterPredicate={
                        isCheck ? isCheckingAccount : undefined
                    }
                    hideArrow={false}
                    buttonSize="lg"
                    buttonProps={{ block: true }}
                />
                <fields.Date
                    name="date"
                    label="Date"
                    className={styles.dateField}
                    required
                    buttonProps={{
                        variant: "primary",
                        size: "lg",
                    }}
                />
                <fields.Money
                    name="amount"
                    label={isCheck ? "Check amount" : "Deposit amount"}
                    className={styles.amountField}
                    clearable
                    emptyValue={undefined}
                    allowNegative={false}
                    size="xs"
                    showCurrencySymbolIfEmpty
                    required
                />
            </HStack>
            <HStack gap={12} align="start">
                <FormikField<string>
                    name="counterpartyId"
                    label="Counterparty"
                    className={styles.payeeField}
                    control="button"
                >
                    {({ helpers, invalid }) => (
                        <CounterpartyCombobox
                            selectedCounterparty={selectedCounterparty}
                            onChange={(counterparty) => {
                                setSelectedCounterparty(counterparty);
                                void helpers.setValue(counterparty?.id ?? "");
                            }}
                            onBlur={() => void helpers.setTouched(true)}
                            invalid={invalid}
                            triggerButtonProps={{ size: "lg", block: true }}
                        />
                    )}
                </FormikField>
                <fields.Text
                    name="checkNumber"
                    label="Reference number"
                    size="lg"
                    placeholder="e.g. 0001"
                    className={styles.referenceField}
                />
                <fields.Text
                    name="memo"
                    label="Memo"
                    size="lg"
                    placeholder="Optional memo"
                    className={styles.memoField}
                />
            </HStack>
        </>
    );
};
